import Vue from 'vue'
import VueRouter from 'vue-router'
import NProgress from 'nprogress/nprogress'

import CookieStorage from '@/utils/cookie-storage'
import { getSystemLocale } from '@/utils/system'
import { normalizeLocale } from '@/utils/i18n'
import { isNetworkError } from '@/utils/network'
import { checkLocalStorage } from '@/utils/local-storage'
import { initializeSharedCredentialsByOtherTamaraApps } from '@/utils/share-token'
import clientLogger from '@/plugins/logger/core'
import LOGGING_EVENTS from '@/constants/logging-events'
import { TRACKING_EVENTS } from '@/constants/tracking'

// XXX backward compat, remove later
import _locator from '@/service-locator'

import { buildAppRoutes } from './routes'

const buildRootRouter = (locator) => {
  const store = locator.store
  const logger = locator.logger

  Vue.use(VueRouter)
  const STATUS = { success: 'success', failed: 'failed' }

  const setAppLocaleMessage = async (locale) => {
    try {
      await store.dispatch('application/setI18nMessageByLocale', locale, {
        root: true,
      })

      return { status: STATUS.success }
    } catch (error) {
      console.error(error)

      CookieStorage.clearStickyLocale()

      return { status: STATUS.failed }
    }
  }

  const router = new VueRouter({
    routes: buildAppRoutes(locator),
    // Use the HTML5 history API (i.e. normal-looking routes)
    // instead of routes with hashes (e.g. example.com/#/about).
    // This may require some server configuration in production:
    // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
    mode: 'history',
    // Simulate native-like scroll behavior when navigating to a new
    // route and using back/forward buttons.
    scrollBehavior(to, from, savedPosition) {
      return savedPosition || { x: 0, y: 0 }
    },
  })

  // Before each route evaluates...
  router.beforeEach(async (routeTo, routeFrom, next) => {
    // If this isn't an initial page load...
    if (routeFrom.name !== null) {
      // Start the route progress bar.
      NProgress.start()
    }

    if (!store.getters['application/hasLocale']) {
      const targetLocale =
        routeTo.query.locale ||
        CookieStorage.getStickyLocale() ||
        getSystemLocale(window.navigator)
      const locale = normalizeLocale(targetLocale)

      const { status } = await setAppLocaleMessage(locale)

      if (status === STATUS.failed) {
        return redirectToMerchantOrNotFound()
      }
    }

    const isFetchedI18nMessage = await store.dispatch(
      'application/isFetchedI18nMessage',
      'common',
      { root: true }
    )

    if (!isFetchedI18nMessage) {
      await store.dispatch('application/fetchMessages', 'common', {
        root: true,
      })
    }

    if (!checkLocalStorage()) {
      logger.error('Browsers or users may disable local storage')
      // Consider to show error message: browser does not support Web Storage
    }

    const checkoutId =
      routeTo.params?.checkoutId ||
      store.state.session.checkoutId ||
      routeTo.query?.checkoutId ||
      routeTo.query?.checkout_id

    // TODO: We will remove this after the new KYC is decoupled from the checkout
    const kycFlow = routeTo.matched.some((route) => route.meta.kycFlow)

    // Check the session if the session is uncheck and checkoutId is available. If is it a kyc flow, we don't need to check the session.
    if (store.state.session.status === 'uncheck' && checkoutId && !kycFlow) {
      // If the session is uncheck, check the session.
      // Checkout flow doesn't route to /checkout/:checkoutId.
      // So, we need to check the session here. The checkout session id was loaded from localStorage.
      try {
        Vue?.prototype?.$trackingService?.TrackEvent(
          TRACKING_EVENTS.GET_SESSION_API_CALLED.EVENT_NAME,
          { checkout_id: checkoutId }
        )
        await store.dispatch('session/checkSession', {
          checkoutId,
        })

        if (store.state.session.status === 'invalid') {
          Vue?.prototype?.$trackingService?.TrackEvent(
            TRACKING_EVENTS.INVALID_CHECKOUT_SESSION.EVENT_NAME,
            { checkout_id: checkoutId }
          )
          return redirectToMerchantOrNotFound()
        }
        if (store.state.auth.shouldValidateSharedToken) {
          initializeSharedCredentialsByOtherTamaraApps(store, router, routeTo)
        }
      } catch (error) {
        logger.error('Check session error', {
          checkoutId,
          error,
          routeTo,
        })

        if (!isNetworkError(error)) {
          return redirectToMerchantOrNotFound()
        }
      }
    }

    // Check if auth is required on this route (including nested routes).
    const authRequired = routeTo.matched.some(
      (route) => route.meta.authRequired
    )

    // If auth isn't required for the route, just continue.
    if (!authRequired) {
      return next()
    }

    // Validate the local user token...
    try {
      const isLoggedIn = store.getters['auth/loggedIn']
      const isDormantCustomer = store.getters['auth/isDormantCustomer']
      const inAppChallengeRequired =
        store.getters['auth/inAppChallengeRequired']
      const validUser =
        isLoggedIn && !isDormantCustomer && !inAppChallengeRequired
          ? await store.dispatch(
              'auth/getProfile',
              { checkoutId, force: false },
              { root: true }
            )
          : null

      return validUser ? next() : redirectToLogin()
    } catch (_) {
      // Redirect to log-in if we got any errors
      return redirectToLogin()
    }

    function redirectToLogin() {
      // Pass the original route to the login component
      next({
        name: 'login',
        query: { ...routeTo.query, redirectFrom: routeTo.path, checkoutId },
      })
    }

    function redirectToMerchantOrNotFound() {
      const sessionErrorData = store.getters['session/sessionErrorData']
      const isOrderSuccess = sessionErrorData?.isOrderSuccess
      const redirectUrl = isOrderSuccess
        ? sessionErrorData?.merchantSuccessUrl
        : sessionErrorData?.merchantFailureUrl
      if (!redirectUrl) {
        return next({ name: '404', query: { ...routeTo.query } })
      }

      clientLogger.log(LOGGING_EVENTS.REDIRECT_TO_MERCHANT, {
        redirectUrl,
        sessionErrorData,
      })
      window.location.replace(redirectUrl)
    }
  })

  router.beforeResolve(async (routeTo, routeFrom, next) => {
    // Create a `beforeResolve` hook, which fires whenever
    // `beforeRouteEnter` and `beforeRouteUpdate` would. This
    // allows us to ensure data is fetched even when params change,
    // but the resolved route does not. We put it in `meta` to
    // indicate that it's a hook we created, rather than part of
    // Vue Router (yet?).
    try {
      // For each matched route...
      for (const route of routeTo.matched) {
        await new Promise((resolve, reject) => {
          // If a `beforeResolve` hook is defined, call it with
          // the same arguments as the `beforeEnter` hook.
          if (route.meta && route.meta.beforeResolve) {
            route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
              // If the user chose to redirect...
              if (args.length) {
                // If redirecting to the same route we're coming from...
                if (routeFrom.name === args[0].name) {
                  // Complete the animation of the route progress bar.
                  NProgress.done()
                }
                // Complete the redirect.
                next(...args)
                reject(new Error('Redirected'))
              } else {
                resolve()
              }
            })
          } else {
            // Otherwise, continue resolving the route.
            resolve()
          }
        })
      }
      // If a `beforeResolve` hook chose to redirect, just return.
    } catch (error) {
      return
    }

    // If we reach this point, continue resolving the route.
    next()
  })

  router.afterEach((routeTo, routeFrom) => {
    NProgress.done()
  })

  locator.router = router
  return router
}

export { buildRootRouter }

// XXX backward compat, remove later on
export default _locator.router
